/**
 * UpsellModal -- A9-10 Tier-upgrade UI.
 *
 * RENAMED from UpgradePrompt in A9 Sprint 3 -- backward-compat preserved.
 *
 * Two trigger modes:
 *   1. Direct invocation: <UpsellModal limitType="messages" usage={...} onClose={...} />
 *   2. Event-driven: <UpsellModal /> (global mount in App.jsx)
 *      Listens for window 'aif:upsell-needed' CustomEvent dispatched by apiClient on 402.
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import { Sheet } from './overlay';
import useTier from '../hooks/useTier';

export default function UpsellModal({
  token: _token,
  limitType,
  usage,
  onClose,
}) {
  const { t } = useTranslation();
  const { pricing } = useTier();
  const [loading, setLoading] = useState(false);

  // Event-driven state
  const [eventPayload, setEventPayload] = useState(null);
  const [isOpen, setIsOpen] = useState(!!limitType || !!usage);

  useEffect(() => {
    const handler = (e) => {
      setEventPayload(e.detail || {});
      setIsOpen(true);
    };
    window.addEventListener('aif:upsell-needed', handler);
    return () => window.removeEventListener('aif:upsell-needed', handler);
  }, []);

  const close = () => {
    setIsOpen(false);
    setEventPayload(null);
    onClose?.();
  };

  const handleUpgrade = async (targetTier) => {
    setLoading(true);
    try {
      const result = await apiClient.post('/api/v1/aif/billing/create-checkout-session', {
        price_id: targetTier === 'premium' ? 'price_premium_monthly' : 'price_pro_monthly',
        success_url: `${window.location.origin}/billing/success`,
        cancel_url: `${window.location.origin}/billing/cancel`,
      }, { queueOnOffline: true });
      const url = result?.data?.checkout_url || result?.checkout_url;
      if (url) window.location.href = url;
    } catch { /* ApiError */ }
    setLoading(false);
  };

  if (!isOpen) return null;

  let title, detail, upgradeTo, priceEur;

  if (eventPayload) {
    if (eventPayload.code === 'tier_upgrade_required') {
      title = t('upsell.feature_locked_title', { defaultValue: 'Premium-Funktion gesperrt' });
      detail = eventPayload.message || t('upsell.feature_locked_detail', { defaultValue: 'Bitte upgraden Sie.' });
      upgradeTo = eventPayload.upgrade_to || 'premium';
      priceEur = eventPayload.price_eur_monthly || pricing?.[upgradeTo]?.monthly || null;
    } else if (eventPayload.tier && eventPayload.limit !== undefined) {
      title = t('upsell.rate_limit_title', { defaultValue: 'Tageslimit erreicht' });
      detail = t('upsell.rate_limit_detail', {
        used: eventPayload.used || 0,
        limit: eventPayload.limit || 0,
        defaultValue: 'Limit erreicht.',
      });
      upgradeTo = eventPayload.tier === 'free' ? 'premium' : 'pro';
      priceEur = pricing?.[upgradeTo]?.monthly || null;
    } else {
      title = t('upsell.title', { defaultValue: 'Tarif-Upgrade' });
      detail = eventPayload.message || t('upsell.feature_locked_detail');
      upgradeTo = 'premium';
      priceEur = pricing?.premium?.monthly || 29;
    }
  } else {
    const LIMIT_MESSAGES = {
      messages: {
        title: t('billing.limitMessages', { defaultValue: 'Tageslimit erreicht' }),
        detail: t('billing.limitMessagesDetail', { used: usage?.used || 0, limit: usage?.limit || 10 }),
      },
      fields: {
        title: t('billing.limitFields', { defaultValue: 'Feldanzahl-Limit erreicht' }),
        detail: t('billing.limitFieldsDetail', { used: usage?.used || 0, limit: usage?.limit || 5 }),
      },
      feature: {
        title: t('billing.limitFeature', { defaultValue: 'Premium-Feature' }),
        detail: t('billing.limitFeatureDetail'),
      },
    };
    const m = LIMIT_MESSAGES[limitType] || LIMIT_MESSAGES.feature;
    title = m.title;
    detail = m.detail;
    upgradeTo = 'premium';
    priceEur = pricing?.premium?.monthly || 29;
  }

  return (
    <Sheet.Root open={isOpen} onOpenChange={(o) => !o && close()}>
      <Sheet.Content side="bottom" size="md" title={title} data-testid="upsell-modal">
        <Sheet.Body>
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-3xl mb-2" aria-hidden="true">&#11088;</div>
              <p className="text-sm text-[var(--aif-text-muted)] mt-1" data-testid="upsell-detail">
                {detail}
              </p>
            </div>

            <div className="text-center">
              <span className="text-2xl font-bold text-[var(--aif-text-primary)]" data-testid="upsell-price">
                {priceEur} EUR
              </span>
              <span className="text-sm text-[var(--aif-text-muted)]">
                /{t('billing.month', { defaultValue: 'Monat' })}
              </span>
            </div>

            <div className="space-y-2">
              <button
                onClick={() => handleUpgrade(upgradeTo)}
                disabled={loading}
                data-testid="upsell-cta-upgrade"
                className="w-full py-3 rounded-xl bg-[var(--aif-color-primary)] text-white font-medium text-sm hover:bg-[var(--aif-color-primary-dark)] transition disabled:opacity-50 aif-touch-target-lg"
              >
                {loading
                  ? '...'
                  : t('upsell.cta_upgrade', {
                      tier: upgradeTo,
                      price: priceEur,
                      defaultValue: `Upgrade auf ${upgradeTo} (${priceEur} EUR/Monat)`,
                    })}
              </button>
              <button
                onClick={close}
                data-testid="upsell-cta-close"
                className="w-full py-2.5 text-sm text-[var(--aif-text-muted)] aif-touch-target"
              >
                {t('upsell.cta_later', { defaultValue: 'Später' })}
              </button>
            </div>
          </div>
        </Sheet.Body>
      </Sheet.Content>
    </Sheet.Root>
  );
}

// Backward-compat alias
export { UpsellModal as UpgradePrompt };
